import React, { useState } from 'react';
import { CheckCircle, Minus, Plus, ChevronDown, ChevronUp } from 'lucide-react';

const Nutrition = () => {
  const [selectedFlavour, setSelectedFlavour] = useState('original');
  const [selectedSize, setSelectedSize] = useState('1.25kg');
  const [quantity, setQuantity] = useState(1);
  const [expandedNutrient, setExpandedNutrient] = useState(null);

  const prices = {
    '500g': 59,
    '1.25kg': 119
  };

  const servings = {
    '500g': 10,
    '1.25kg': 25
  };

  const totalPrice = prices[selectedSize] * quantity;

  const nutrients = [
    { name: 'Vitamin A', benefit: 'Vision, skin health' },
    { name: 'Vitamin B1 (Thiamine)', benefit: 'Energy metabolism, nerve function' },
    { name: 'Vitamin B2 (Riboflavin)', benefit: 'Cell growth, energy production' },
    { name: 'Vitamin B3 (Niacin)', benefit: 'Supports digestive & nervous systems' },
    { name: 'Vitamin B5 (Pantothenic Acid)', benefit: 'Hormone & cholesterol balance' },
    { name: 'Vitamin B6', benefit: 'Brain health, protein metabolism' },
    { name: 'Vitamin B12', benefit: 'Red blood cell production, nerve function' },
    { name: 'Folic Acid', benefit: 'DNA synthesis, prenatal support' },
    { name: 'Vitamin C', benefit: 'Immunity, collagen formation' },
    { name: 'Vitamin D', benefit: 'Bone strength, calcium absorption' },
    { name: 'Vitamin E', benefit: 'Antioxidant, skin repair' },
    { name: 'Biotin', benefit: 'Hair, skin, and metabolic function' },
    { name: 'Vitamin K', benefit: 'Blood clotting, bone support' },
    { name: 'Chromium', benefit: 'Blood sugar regulation' },
    { name: 'Zinc', benefit: 'Immunity, healing' },
    { name: 'Selenium', benefit: 'Antioxidant, immune function' }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#F5F1E8] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
              The Formulation
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-[#2C2416] mb-6 leading-tight">
              The power is in <br />the nutrition.
            </h1>
            <p className="text-xl text-[#5C4033] max-w-3xl mx-auto">
              Protein. Sumodulin™. Zinc. Essential vitamins and minerals. Every serving, a nutritional foundation for your day.
            </p>
          </div>

          {/* Product Selector */}
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left - Product Image */}
              <div className="bg-[#F5F1E8] p-8 flex items-center justify-center">
                <img
                  src={selectedFlavour === 'original' 
                    ? "https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273867014_877dc38a.png"
                    : "https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273848420_b8fd2efd.png"
                  }
                  alt={`GUAG ${selectedFlavour}`}
                  className="w-full max-w-sm h-auto"
                />
              </div>

              {/* Right - Product Options */}
              <div className="p-8">
                {/* Flavour Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#5C4033] mb-3">
                    Select Flavour
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSelectedFlavour('original')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedFlavour === 'original'
                          ? 'border-[#8B6F47] bg-[#F5F1E8]'
                          : 'border-gray-200 hover:border-[#8B6F47]'
                      }`}
                    >
                      <div className="font-bold text-[#2C2416] mb-1">Original</div>
                      <div className="text-xs text-[#8B6F47]">Classic flavour</div>
                    </button>
                    <button
                      onClick={() => setSelectedFlavour('chocolate')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedFlavour === 'chocolate'
                          ? 'border-[#8B6F47] bg-[#F5F1E8]'
                          : 'border-gray-200 hover:border-[#8B6F47]'
                      }`}
                    >
                      <div className="font-bold text-[#2C2416] mb-1">Chocolate</div>
                      <div className="text-xs text-[#8B6F47]">Rich & smooth</div>
                    </button>
                  </div>
                </div>

                {/* Pack Size Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#5C4033] mb-3">
                    Select Pack Size
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSelectedSize('500g')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedSize === '500g'
                          ? 'border-[#8B6F47] bg-[#F5F1E8]'
                          : 'border-gray-200 hover:border-[#8B6F47]'
                      }`}
                    >
                      <div className="font-bold text-[#2C2416] mb-1">500g</div>
                      <div className="text-xs text-[#8B6F47]">10 servings</div>
                      <div className="text-sm font-semibold text-[#2C2416] mt-1">R59</div>
                    </button>
                    <button
                      onClick={() => setSelectedSize('1.25kg')}
                      className={`p-4 rounded-xl border-2 transition-all relative ${
                        selectedSize === '1.25kg'
                          ? 'border-[#8B6F47] bg-[#F5F1E8]'
                          : 'border-gray-200 hover:border-[#8B6F47]'
                      }`}
                    >
                      <span className="absolute -top-2 -right-2 bg-[#8B6F47] text-white text-xs px-2 py-1 rounded-full">
                        Best Value
                      </span>
                      <div className="font-bold text-[#2C2416] mb-1">1.25kg</div>
                      <div className="text-xs text-[#8B6F47]">25 servings</div>
                      <div className="text-sm font-semibold text-[#2C2416] mt-1">R119</div>
                    </button>
                  </div>
                </div>

                {/* Price Display */}
                <div className="bg-[#F5F1E8] rounded-xl p-4 mb-6">
                  <div className="text-2xl font-bold text-[#2C2416] mb-1">
                    R{totalPrice}
                  </div>
                  <div className="text-sm text-[#8B6F47]">
                    per pack ({servings[selectedSize]} servings)
                  </div>
                </div>

                {/* Quantity Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#5C4033] mb-3">
                    Quantity:
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-[#F5F1E8] rounded-lg flex items-center justify-center text-[#8B6F47] hover:bg-[#8B6F47] hover:text-white transition-colors"
                    >
                      <Minus size={20} />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="flex-1 text-center text-xl font-bold text-[#2C2416] bg-[#F5F1E8] rounded-lg px-4 py-2"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 bg-[#F5F1E8] rounded-lg flex items-center justify-center text-[#8B6F47] hover:bg-[#8B6F47] hover:text-white transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button className="w-full px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg">
                  Add to Cart — R{totalPrice}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Nationwide Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
            Available Nationwide
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-12">
            Find GUAG at Your Favourite Store
          </h2>
          <p className="text-xl text-[#5C4033] mb-12">
            GUAG is proudly stocked by South Africa's leading retailers. Pick up a pack on your next shop.
          </p>

          {/* Retailer Logos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="flex items-center justify-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769277649387_11b24340.png"
                alt="Checkers"
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769277654847_979dd117.png"
                alt="Pick n Pay"
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769277656137_215b2bce.webp"
                alt="SPAR"
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769277652548_f78e09b3.png"
                alt="Dis-Chem"
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all"
              />
            </div>
          </div>

          {/* Request GUAG CTA */}
          <div className="bg-[#F5F1E8] rounded-2xl p-12 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#2C2416] mb-4">
              Can't find GUAG?
            </h3>
            <p className="text-[#5C4033] mb-6">
              Ask your local store to stock it
            </p>
            <button className="px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium">
              Request GUAG
            </button>
            <p className="text-sm text-[#8B6F47] mt-6 italic">
              Mission: Expand the GUAG Network<br />
              Every request brings GUAG closer to someone who needs it.
            </p>
          </div>
        </div>
      </section>

      {/* Daily Nutrition Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              Daily Nutrition
            </h2>
            <p className="text-xl text-[#5C4033] mb-8">
              Real ingredients. Real benefits. Real clarity.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                RS · Reduced Sugar
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                HF · High Fibre
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                GF · Gluten Free
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                NG · Non-GMO
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                PB · Plant-Based
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                NS · No Added Salt
              </span>
              <span className="px-4 py-2 bg-white border border-[#E5DCC8] rounded-full text-xs font-medium text-[#5C4033]">
                2-IN-1 · Bowl or Shake
              </span>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">Protein</h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Supports muscle growth and repair</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Promotes satiety and sustained energy</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">Sumodulin™</h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Plant-derived bioactive extract</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Supports immune resilience</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Exclusive to GUAG</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">Zinc</h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Critical for immune function</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Supports growth and development</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Essential for growing children and adults</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">Vitamins & Minerals</h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">21 essential micronutrients</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Supports cognitive performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm">Supports recovery and resilience</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Product Images */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273874523_f7a49c81.png"
                alt="GUAG Both Flavours"
                className="w-full h-auto rounded-2xl mb-4"
              />
              <h3 className="font-bold text-[#2C2416] mb-2">Two Flavours</h3>
              <p className="text-sm text-[#8B6F47]">Original & Chocolate</p>
            </div>
            <div className="text-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273867014_877dc38a.png"
                alt="GUAG Original Bowl"
                className="w-full h-auto rounded-2xl mb-4"
              />
              <h3 className="font-bold text-[#2C2416] mb-2">Bowl Format</h3>
              <p className="text-sm text-[#8B6F47]">60 seconds · Sustained energy</p>
            </div>
            <div className="text-center">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273848420_b8fd2efd.png"
                alt="GUAG Chocolate Shake"
                className="w-full h-auto rounded-2xl mb-4"
              />
              <h3 className="font-bold text-[#2C2416] mb-2">Shake Format</h3>
              <p className="text-sm text-[#8B6F47]">60 seconds · No compromise</p>
            </div>
          </div>
        </div>
      </section>

      {/* Deployment Protocol Section */}
      <section className="py-20 bg-[#2C2416] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#C9A961] text-sm tracking-widest uppercase mb-4">
              OPERATOR'S MANUAL
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              DEPLOYMENT PROTOCOL
            </h2>
            <p className="text-xl text-gray-300">
              Two formats. One mission. Choose your method.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Method A - Porridge */}
            <div className="bg-[#3D3426] rounded-2xl p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-[#C9A961] text-sm font-semibold">Method A</span>
                  <h3 className="text-3xl font-bold mt-2">PORRIDGE</h3>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-[#C9A961]">60</div>
                  <div className="text-sm">Seconds</div>
                </div>
              </div>

              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <div className="font-bold mb-1">Measure</div>
                    <div className="text-gray-300 text-sm">3 heaped tablespoons (50g)</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <div className="font-bold mb-1">Add Liquid</div>
                    <div className="text-gray-300 text-sm">125ml (½ cup) hot or cold water/milk</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <div className="font-bold mb-1">Execute</div>
                    <div className="text-gray-300 text-sm">Stir thoroughly. Consume.</div>
                  </div>
                </div>
              </div>

              <div className="text-sm text-[#C9A961]">
                Optimal for: Sustained energy · Morning ritual · Full satiety
              </div>
            </div>

            {/* Method B - Shake */}
            <div className="bg-[#3D3426] rounded-2xl p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-[#C9A961] text-sm font-semibold">Method B</span>
                  <h3 className="text-3xl font-bold mt-2">SHAKE</h3>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-[#C9A961]">60</div>
                  <div className="text-sm">Seconds</div>
                </div>
              </div>

              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <div className="font-bold mb-1">Measure</div>
                    <div className="text-gray-300 text-sm">3 heaped tablespoons (50g)</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <div className="font-bold mb-1">Add Liquid</div>
                    <div className="text-gray-300 text-sm">200ml hot or cold water/milk</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8B6F47] rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <div className="font-bold mb-1">Execute</div>
                    <div className="text-gray-300 text-sm">Shake vigorously. Deploy.</div>
                  </div>
                </div>
              </div>

              <div className="text-sm text-[#C9A961]">
                Optimal for: Rapid deployment · On-the-move · Zero compromise
              </div>
            </div>
          </div>

          <p className="text-center text-gray-300 mt-12 text-lg">
            Same formulation. Same nutrition. Your mission, your method.
          </p>
        </div>
      </section>

      {/* Micro Nutrient Insight Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
              Deep Dive
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              MICRO NUTRIENT INSIGHT
            </h2>
            <p className="text-xl text-[#5C4033] mb-8">
              What's inside each bowl? Click to explore.
            </p>
          </div>

          <div className="bg-[#F5F1E8] rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-[#2C2416] mb-6">
              Clinical Formulation
            </h3>
            <h4 className="text-xl font-bold text-[#5C4033] mb-4">
              Every Nutrient. Every Purpose.
            </h4>
            <p className="text-[#5C4033] mb-8">
              No filler. No fluff. Just 23 essential micronutrients working together to support your body's real needs. 
              This is what functional nutrition looks like.
            </p>

            {/* Nutrients List */}
            <div className="grid md:grid-cols-2 gap-4">
              {nutrients.map((nutrient, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setExpandedNutrient(expandedNutrient === index ? null : index)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-[#2C2416]">{nutrient.name}</div>
                      <div className="text-sm text-[#8B6F47] mt-1">{nutrient.benefit}</div>
                    </div>
                    {expandedNutrient === index ? (
                      <ChevronUp className="text-[#8B6F47]" size={20} />
                    ) : (
                      <ChevronDown className="text-[#8B6F47]" size={20} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sumodulin Feature */}
          <div className="bg-[#2C2416] text-white rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-12">
                <span className="inline-block px-3 py-1 bg-[#8B6F47] rounded-full text-xs font-semibold mb-4">
                  Exclusive to GUAG
                </span>
                <h3 className="text-3xl font-bold mb-4">Sumodulin™</h3>
                <p className="text-gray-300 mb-6">
                  <strong>Powered by Pelargonium sidoides</strong> — a potent plant extract used traditionally 
                  for immune support and respiratory health. This isn't a marketing gimmick. It's a cornerstone 
                  of GUAG's health-first formula, backed by traditional use and modern research.
                </p>
                <blockquote className="border-l-4 border-[#8B6F47] pl-4 italic text-xl mb-8">
                  "You're not just eating breakfast. You're building a foundation."
                </blockquote>
                <p className="text-gray-300">
                  This is what you're investing in. Nutrition with consistency and accountability. 
                  Designed to support learning, strength, and recovery.
                </p>
              </div>
              <div className="bg-[#3D3426] flex items-center justify-center p-8">
                <img
                  src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273774798_48be9878.png"
                  alt="Sumodulin"
                  className="w-full max-w-sm h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
            Ready to fuel your best day?
          </h2>
          <p className="text-xl text-[#5C4033] mb-12">
            Order GUAG today and experience the difference real nutrition makes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg">
              Buy GUAG
            </button>
            <a
              href="/project"
              className="px-8 py-4 bg-white border-2 border-[#8B6F47] text-[#8B6F47] rounded-full hover:bg-[#8B6F47] hover:text-white transition-colors font-medium text-lg"
            >
              GUAG/PROJECT
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Nutrition;
